import PageHeader from '../../../components/PageHeader'
import MainLayout from '../../../layouts/MainLayout'
import { usePermissions } from '../../../hooks/usePermissions'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useUrlFilters from '../../../hooks/useUrlFilters'
import useFilterHandlers from '../../../hooks/useFilterHandlers'
import useGenericFilter from '../../../hooks/filters/useGenericFilter'
import ListHeader from '../../../components/ListHeader'
import TableWithInactiveFilter from '../../../components/table/TableWithInactiveFilter'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import useBaseService from '../../../hooks/services/useBaseService'
import useNotification from '../../../hooks/useNotification'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import useLoader from '../../../hooks/useLoader'
import { entities } from '../../../constants/entities'
import useAction from '../../../hooks/useAction'
import { useCallback, useEffect, useState } from 'react'
import {
  faEdit,
  faEye,
  faPrint,
  faTags,
  faTrash,
  faUndo,
} from '@fortawesome/free-solid-svg-icons'
import { formatDate } from '../../../utils/formatDate'
import FilterCollapse from '../../../components/FilterCollapse'
import { inputsFiltersTransfer } from './filters/filtres-transactions'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { DialogExportProtocolTransaction } from '../../Movements/Movement/components/DialogExportProtocolTransaction'
import { useOrgan } from '../../../hooks/useOrgan'
import { useReport } from '../../../context/ReportContext'

const TransferTransactionPage = () => {
  const { canAccess } = usePermissions()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [openModalExportProtocol, setOpenModalExportProtocol] = useState(false)
  const [dataToExport, setDataToExport] = useState(null)

  const { selectedOrgan } = useOrgan()

  const { setExportId } = useReport()

  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      id: [],
      'items.address.alias': [], // Apelido do endereço
      'items.address.street': [], // Rua do endereço
      'items.address.city': [], // Cidade do endereço
      'items.address.state': [], // Estado do endereço
      sender_id: selectedOrgan?.id,
      deleted_at: false,
    },
    'almox_transactions'
  )

  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters)

  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        id: [],
        'items.address.alias': [], // Apelido do endereço
        'items.address.street': [], // Rua do endereço
        'items.address.city': [], // Cidade do endereço
        'items.address.state': [], // Estado do endereço
        sender_id: selectedOrgan?.id,
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear
    )

  const { get: baseServiceGet, del: baseServiceDelete } =
    useBaseService(navigate)
  const { hideLoader, showLoader } = useLoader()
  const { showNotification } = useNotification()

  const {
    data: transactionsData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQueryWithLoader({
    queryKey: ['almox_transactions', filters, pagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filters)
      const params = {
        filters: {
          ...filtersTransformed,
          transaction_type_id: {
            $eq: 2, // TRANSFERENCIA
          },
          sender_id: {
            $ne: null,
          },
        },
        page: pagination.page,
        perPage: pagination.perPage,
      }
      const response = await baseServiceGet(entities.transactions.get, params)
      return response.result
    },
    refetchOnMount: true,
    select: (data) => {
      return {
        transactions: data?.data?.map((transaction) => ({
          id: transaction.id,
          type: transaction.type?.name || '-',
          sender: transaction.sender?.name || '-',
          receiver: transaction.receiver?.name || '-',
          isDeleted: !!transaction.deleted_at,
          created_by: transaction.user?.name || '-',
          transaction_date: transaction.transaction_date
            ? formatDate({
                rawDate: transaction.transaction_date,
                showTime: false,
              })
            : '-',
          user: transaction.user?.name || '-',
          hasEquipments: transaction.has_equipments,
        })),
        totalPages: data?.last_page,
      }
    },
    placeholderData: (prev) => prev,
  })

  const { transactions = [], totalPages } = transactionsData || {}

  const columns = [
    { key: 'id', label: 'Número' },
    { key: 'sender', label: 'Estoque de Saída' },
    { key: 'receiver', label: 'Estoque de Entrada' },
    { key: 'transaction_date', label: 'Data da Transação' },
    { key: 'user', label: 'Responsável' },
  ]

  useEffect(() => {
    isLoading ? showLoader() : hideLoader()
  }, [isLoading, showLoader, hideLoader])

  useEffect(() => {
    if (isError) {
      console.error('Erro ao carregar insumos:', error)
      showNotification(
        'error',
        `Erro ao carregar insumos: ${
          error.response?.data?.message || error.message
        }`
      )
    }
  }, [isError, error, showNotification])

  const {
    openModalConfirmation,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
    action,
  } = useAction(navigate)

  useEffect(() => {
    if (location.state?.message) {
      showNotification('error', location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  const refreshList = useCallback(() => {
    queryClient.invalidateQueries(['almox_transactions', filters, pagination])
  }, [queryClient, filters, pagination])

  const inactivateTransactionMutation = useMutation({
    mutationFn: async (id) => {
      showLoader()
      const response = await baseServiceDelete(entities.transactions.delete(id))
      return response
    },
    onSuccess: (res) => {
      if (res?.status === 200) refreshList()
    },
    onError: (err) => {
      console.error('onError ', err)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
      handleCancelConfirmation()
    },
  })

  const activateTransactionMutation = inactivateTransactionMutation

  const exportTags = useMutation({
    mutationFn: async ({ transactionId }) => {
      showLoader()
      const params = {}
      const response = await baseServiceGet(
        entities.reports.tagProductFromTransaction(transactionId),
        params
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 202 || response?.status === 200) {
        const generated_id = response.result?.export_id

        if (generated_id) {
          setExportId(generated_id)
          localStorage.setItem('report_exportId', generated_id.toString())
        }
      }
    },
    onError: (error) => {
      console.error('onError ', error)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
    },
  })

  const actions = [
    {
      id: 'exportar_etiquetas',
      icon: faTags,
      title: 'Imprimir Etiquetas',
      buttonClass: 'btn-success',
      permission: 'Ver transações',
      onClick: async (transaction) => {
        try {
          await exportTags.mutateAsync({ transactionId: transaction.id })
        } catch (error) {
          console.error('Erro ao exportar etiquetas:', {
            status: error.status,
            message: error.message,
          })
        }
      },
      show: (transaction) =>
        !transaction.isDeleted &&
        !transaction.hasEquipments &&
        canAccess('Ver transações'),
    },
    {
      id: 'exportar_protocolo',
      icon: faPrint,
      title: 'Imprimir Protocolo',
      buttonClass: 'btn-success',
      permission: 'Ver movimentos',
      onClick: (transaction) => {
        setOpenModalExportProtocol(true)
        setDataToExport(transaction)
      },
      show: (transaction) =>
        !transaction.isDeleted && canAccess('Ver transações'),
    },
    {
      id: 'details',
      icon: faEye,
      title: 'Ver Detalhes',
      buttonClass: 'btn-info',
      permission: 'Ver transações',
      onClick: (product) =>
        navigate(`/almoxarifado/transacoes/detalhes/${product.id}`),
      show: (product) => !product.isDeleted && canAccess('Ver transações'),
    },
    {
      id: 'edit',
      icon: faEdit,
      title: 'Editar',
      buttonClass: 'btn-primary',
      permission: 'Atualizar transações',
      onClick: (product) =>
        navigate(
          `/almoxarifado/transacoes/editar/${product.id}?type=transferencia`
        ),
      show: (product) =>
        !product.isDeleted && canAccess('Atualizar transações'),
    },
    {
      id: 'delete',
      icon: faTrash,
      title: 'Excluir',
      buttonClass: 'btn-danger',
      permission: 'Excluir transações',
      onClick: (product) =>
        showConfirmation(
          product,
          `Você tem certeza que deseja excluir?`,
          async () => {
            try {
              await inactivateTransactionMutation.mutateAsync(product.id)
            } catch (error) {
              console.error('Erro ao excluir transação:', {
                status: error.status,
                message: error.message,
              })
            }
          }
        ),
      show: (product) => !product.isDeleted && canAccess('Excluir transações'),
    },
    {
      id: 'activate',
      icon: faUndo,
      title: 'Restaurar',
      buttonClass: 'btn-info',
      permission: 'Excluir transações',
      onClick: (product) =>
        showConfirmation(
          product,
          `Você tem certeza que deseja restaurar?`,
          async () => {
            try {
              await activateTransactionMutation.mutateAsync(product.id)
            } catch (error) {
              console.error('Erro ao restaurar transação:', {
                status: error.status,
                message: error.message,
              })
            }
          }
        ),
      show: (product) => product.isDeleted && canAccess('Excluir transações'),
    },
  ]

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Transações" showBackButton backUrl="/dashboard" />
      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsFiltersTransfer({
            localFilterValues,
            handleFilterChange,
          })}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        <ListHeader
          title="Lista de Transações de Transferência"
          buttonText="Cadastrar Transação"
          buttonLink="/almoxarifado/transacoes/criar?type=transferencia"
          permission="Criar transações"
          canAccess={canAccess}
        />

        <TableWithInactiveFilter
          data={transactions}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setPagination({ page: 1, perPage: newPerPage })
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="default"
          isLoading={isLoading}
          isFetching={isFetching}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem?.name || ''}
          text={action?.text}
        />

        {/* Modal de Exportar Protocolo */}
        <DialogExportProtocolTransaction
          data={dataToExport}
          open={openModalExportProtocol}
          onClose={() => setOpenModalExportProtocol(false)}
        />
      </div>
    </MainLayout>
  )
}

export default TransferTransactionPage
